import { useState, useEffect, useRef, type ChangeEvent, useMemo } from 'react';
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Users,
  Upload,
  Tag,
  GitMerge,
  Mail,
  GitBranch,
  Sparkles,
  GitBranchPlus,
  Columns3,
  Video,
  User,
  Pencil,
  Trash2,
} from 'lucide-react';
import { SortableTh, EnrichColumnTh } from '@shared/components/ui/SortableTableHeader';
import { TableAvatar } from '@shared/components/ui/TableAvatar';
import { RowHoverActions } from '@shared/components/ui/RowHoverActions';
import { useAuth } from '@core/auth/useAuth';
import { usePermissions } from '@core/auth/usePermissions';
import { queryKeys } from '@core/query/queryKeys';
import { supabase } from '@core/supabase/client';
import {
  candidateService,
  type CandidateListResult,
  type CandidateSortColumn,
} from '@core/supabase/services/candidateService';
import { candidateTagService } from '@core/supabase/services/candidateTagService';
import { applicationService } from '@core/supabase/services/applicationService';
import { parseCandidateSpreadsheet, type CandidateImportRow } from '@core/candidates/parseCandidateSpreadsheet';
import {
  Candidate,
  ApplicationStatus,
  REJECTION_REASON_CODES,
  REJECTION_REASON_LABELS,
  type RejectionReasonCode,
  type CandidateSource,
  CANDIDATE_SOURCE_LABELS,
} from '@core/types';
import { ALL_STATUSES, PIPELINE_STATUS_LABEL } from '@core/constants/pipelineStages';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { SyncStatusBadge } from '@shared/components/SyncStatusBadge';
import { useToast } from '@shared/hooks/useToast';
import { ListPreviewDrawer } from '@shared/components/ui/ListPreviewDrawer';
import { RecordHeader } from '@shared/components/ui/RecordHeader';
import { RecordMetaCard } from '@shared/components/ui/RecordMetaCard';
import { formatCreatedWhen } from '@shared/lib/formatCreatedWhen';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { AssistantIntake } from '@modules/agent/components/AssistantIntake';
import { INTAKE_CONFIGS } from '@modules/agent/intake.config';
import { BulkComposePanel } from '@shared/components/BulkComposePanel';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { TablePagination } from '@shared/components/ui/TablePagination';
import { DataTableToolbar } from '@shared/components/ui/DataTableToolbar';
import { DataTableDeleteConfirmModal } from '@shared/components/ui/DataTableDeleteConfirmModal';
import { CandidateMergeModal } from './CandidateMergeModal';
import { CandidateBulkFitmentModal } from './CandidateBulkFitmentModal';
import { Modal } from '@shared/components/ui/Modal';
import { ContextAiAssistModal } from '@shared/components/ai/ContextAiAssistModal';
import {
  CandidatesOperationsDropdown,
  CandidatesCustomizeColumnsModal,
  CandidatesTableRightsModal,
} from './CandidatesListChrome';
import {
  loadCandidatesTablePolicy,
  defaultCandidatesTablePolicy,
  maskEmailValue,
  maskPhoneValue,
  EXTRA_COLUMN_META,
  normalizeCandidateListUserAccess,
  type CandidatesTablePolicy,
  type ExtraColumnKey,
  type BaseColumnKey,
} from './candidatesListPreferences';
import {
  defaultExportMeta,
  exportCandidatesCsv,
  exportCandidatesPdf,
  exportCandidatesXlsx,
} from './candidatesExportFormats';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { aiInterviewSessionService } from '@core/supabase/services/aiInterviewSessionService';
import { SmartMatchRedirectCTA } from '@modules/smart-match/SmartMatchRedirectCTA';

const STATUSES_REQUIRING_REJECTION: ApplicationStatus[] = ['rejected', 'offer_rejected'];

function previewInitials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

export function CandidatesPage() {
  const { user } = useAuth();
  const perms = usePermissions();
  const { toast, showToast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const orgId = user?.orgId;

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<CandidateSortColumn>('created_at');
  const [sortAsc, setSortAsc] = useState(false);
  const [tablePolicy, setTablePolicy] = useState<CandidatesTablePolicy>(defaultCandidatesTablePolicy);
  const [rightsOpen, setRightsOpen] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [rowMenu, setRowMenu] = useState<{ x: number; y: number; candidateId: string } | null>(null);
  const rowMenuRef = useRef<HTMLDivElement>(null);

  const listParams = useMemo(
    () => ({
      search: search || undefined,
      page,
      pageSize,
      sortBy,
      sortAsc,
    }),
    [search, page, pageSize, sortBy, sortAsc],
  );

  useEffect(() => {
    if (orgId) setTablePolicy(loadCandidatesTablePolicy(orgId));
  }, [orgId]);

  const handleSortColumn = (col: CandidateSortColumn) => {
    if (sortBy === col) {
      setSortAsc((a) => !a);
    } else {
      setSortBy(col);
      setSortAsc(col === 'created_at' ? false : true);
    }
    setPage(1);
  };

  const candidatesQuery = useQuery({
    queryKey: orgId ? queryKeys.candidates.list(orgId, listParams) : ['candidates', '__none'],
    queryFn: () => candidateService.list(orgId!, listParams) as Promise<CandidateListResult>,
    enabled: Boolean(orgId),
    placeholderData: keepPreviousData,
  });

  const candidates = candidatesQuery.data?.data ?? [];
  const totalItems = candidatesQuery.data?.total ?? 0;
  const candidatesIntakeCfg = useMemo(
    () => INTAKE_CONFIGS.find((c) => c.route === '/candidates') ?? null,
    [],
  );
  const loading = Boolean(orgId && candidatesQuery.isPending);
  const headerCheckRef = useRef<HTMLInputElement>(null);
  const [bulkTagInput, setBulkTagInput] = useState('');
  const [bulkTagging, setBulkTagging] = useState(false);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showBulkPipelineModal, setShowBulkPipelineModal] = useState(false);
  const [bulkAppIds, setBulkAppIds] = useState<string[]>([]);
  const [bulkPipelineStatus, setBulkPipelineStatus] = useState<ApplicationStatus>('shortlisted');
  const [bulkRejectionCode, setBulkRejectionCode] = useState<RejectionReasonCode>('not_qualified');
  const [bulkRejectionText, setBulkRejectionText] = useState('');
  const [bulkPipelineBusy, setBulkPipelineBusy] = useState(false);
  const [bulkPipelineErr, setBulkPipelineErr] = useState('');
  const [showBulkFitmentModal, setShowBulkFitmentModal] = useState(false);
  const importFileRef = useRef<HTMLInputElement>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importChoiceOpen, setImportChoiceOpen] = useState(false);
  const [importModalMode, setImportModalMode] = useState<'menu' | 'resumes'>('menu');
  const [roleFitJobByCandidate, setRoleFitJobByCandidate] = useState<Record<string, string>>({});
  const [sourcingAiOpen, setSourcingAiOpen] = useState(false);
  const [interviewPrepBusy, setInterviewPrepBusy] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const sourcingPoolContext = useMemo(() => {
    const sample = candidates
      .slice(0, 15)
      .map(
        (c) =>
          `${c.full_name ?? '—'} · ${c.current_title ?? '—'} @ ${c.current_company ?? '—'} · ${(c.parsed_skills ?? []).slice(0, 5).join(', ')}`
      )
      .join('\n');
    return `List search: ${search || '—'}\nPage ${page} · showing ${candidates.length} row(s)\nSample:\n${sample || '(empty page)'}`;
  }, [candidates, search, page]);

  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate = candidates.length > 0 && selectedIds.size > 0 && selectedIds.size < candidates.length;
    }
  }, [candidates.length, selectedIds.size]);

  useEffect(() => {
    if (!rowMenu) return;
    const close = (e: MouseEvent) => {
      const t = e.target as Node;
      if (rowMenuRef.current?.contains(t)) return;
      setRowMenu(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [rowMenu]);

  useEffect(() => {
    const candidateList = candidatesQuery.data?.data;
    if (!user?.orgId || !candidateList || candidateList.length === 0) {
      setRoleFitJobByCandidate({});
      return;
    }
    let cancelled = false;
    const ids = candidateList.map((c) => c.id);
    void supabase
      .from('ggj_b2b_applications')
      .select('candidate_id, job_id, submitted_at')
      .eq('org_id', user.orgId)
      .in('candidate_id', ids)
      .order('submitted_at', { ascending: false })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setRoleFitJobByCandidate({});
          return;
        }
        const map: Record<string, string> = {};
        for (const row of data) {
          const cid = row.candidate_id as string;
          const jid = row.job_id as string;
          if (jid && map[cid] == null) map[cid] = jid;
        }
        setRoleFitJobByCandidate(map);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.orgId, candidatesQuery.data]);

  const openBulkPipelineModal = async () => {
    if (!user?.orgId || selectedIds.size === 0) return;
    setBulkPipelineErr('');
    const { data, error } = await supabase
      .from('ggj_b2b_applications')
      .select('id')
      .eq('org_id', user.orgId)
      .in('candidate_id', Array.from(selectedIds));
    if (error) {
      setBulkPipelineErr(error.message);
      return;
    }
    const ids = (data ?? []).map((r) => r.id as string);
    if (ids.length === 0) {
      setBulkPipelineErr('No applications found for the selected candidates.');
      setBulkAppIds([]);
    } else {
      setBulkAppIds(ids);
    }
    setShowBulkPipelineModal(true);
  };

  const handleBulkMailto = () => {
    const emails = candidates
      .filter((c) => selectedIds.has(c.id) && c.email?.trim())
      .map((c) => c.email!.trim());
    if (!emails.length) return;
    const bcc = emails.join(',');
    if (bcc.length > 1900) {
      showToast({ title: 'Too many addresses for a single mail link. Export CSV and use your mail client’s mail merge, or select fewer candidates.', kind: 'error' });
      return;
    }
    window.location.href = `mailto:?bcc=${encodeURIComponent(bcc)}`;
  };

  const buildPayloadFromImportRow = (orgId: string, createdBy: string, row: CandidateImportRow) => ({
    org_id: orgId,
    created_by: createdBy,
    full_name: row.full_name.trim(),
    email: row.email.trim(),
    phone: row.phone,
    linkedin_url: row.linkedin_url,
    source_channel: 'CSV import',
    source: 'bulk_upload' as CandidateSource,
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    current_company: row.current_company,
    current_title: row.current_title,
    location: row.location,
    avatar_url: null,
    base_resume_url: null,
    parsed_skills: row.skills.length ? row.skills : null,
    years_exp: null,
    education: null,
    tags: [],
    notes: null,
    recruiter_rating: null,
    is_active: true,
  });

  const openImportPicker = () => {
    if (importBusy) return;
    importFileRef.current?.click();
  };

  const closeImportModal = () => {
    setImportChoiceOpen(false);
    setImportModalMode('menu');
    if (orgId) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.candidates.all(orgId) });
      void queryClient.invalidateQueries({ queryKey: ['smart-match'] });
    }
  };

  const onImportFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !user?.orgId || !user.id) return;
    setImportBusy(true);
    try {
      const { rows, parseErrors } = await parseCandidateSpreadsheet(file);
      if (parseErrors.length) {
        showToast({ title: `Import issues:\n${parseErrors.slice(0, 8).join('\n')}${parseErrors.length > 8 ? '\n…' : ''}`, kind: 'error' });
      }
      if (rows.length === 0) {
        showToast({ title: 'No importable rows found.', kind: 'error' });
        return;
      }
      let ok = 0;
      const rowErrs: string[] = [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]!;
        try {
          await candidateService.create(
            buildPayloadFromImportRow(user.orgId!, user.id, row) as Parameters<typeof candidateService.create>[0]
          );
          ok += 1;
        } catch (err: unknown) {
          const msg = err instanceof Error ? err.message : String(err);
          rowErrs.push(`Row ${i + 2} (${row.email}): ${msg}`);
        }
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.candidates.all(user.orgId!) });
      const tail = rowErrs.length
        ? `\n\nFailed (${rowErrs.length}):\n${rowErrs.slice(0, 5).join('\n')}${rowErrs.length > 5 ? '\n…' : ''}`
        : '';
      showToast({ title: `Imported ${ok} of ${rows.length} candidate(s).${tail}`, kind: 'error' });
    } catch (err: unknown) {
      showToast({ title: err instanceof Error ? err.message : 'Import failed.', kind: 'error' });
    } finally {
      setImportBusy(false);
    }
  };

  const fetchRowsForExport = async (scope: 'selection' | 'all'): Promise<Candidate[]> => {
    if (!user?.orgId) return [];
    if (scope === 'selection') {
      if (selectedIds.size === 0) return [];
      const fetched = await Promise.all(Array.from(selectedIds).map((id) => candidateService.getById(id)));
      return fetched.filter((c): c is Candidate => c != null && c.org_id === user.orgId);
    }
    const pageSize = 500;
    const first = (await candidateService.list(user.orgId, {
      page: 1,
      pageSize,
      sortBy,
      sortAsc,
    })) as CandidateListResult;
    const all: Candidate[] = [...first.data];
    let page = 2;
    while (all.length < first.total) {
      const next = (await candidateService.list(user.orgId, {
        page,
        pageSize,
        sortBy,
        sortAsc,
      })) as CandidateListResult;
      all.push(...next.data);
      if (next.data.length === 0) break;
      page += 1;
    }
    return all;
  };

  const runExportFormat = async (fmt: 'csv' | 'xlsx' | 'pdf', scope: 'selection' | 'all') => {
    try {
      const rows = await fetchRowsForExport(scope);
      if (rows.length === 0) {
        showToast({ title: scope === 'selection'
          ? 'Select one or more rows, or use Export all from Operations.'
          : 'No candidates to export.', kind: 'error' });
        return;
      }
      const base = `candidates_${new Date().toISOString().slice(0, 10)}`;
      const meta = defaultExportMeta({
        recordCount: rows.length,
        reportTitle: 'Candidates',
        subtitle:
          scope === 'selection'
            ? rows.length === 1
              ? 'Single candidate — full record'
              : `Selected · ${rows.length} record(s)`
            : 'All active candidates in organization',
        generatedAt: new Date(),
      });
      if (fmt === 'csv') exportCandidatesCsv(rows, `${base}.csv`);
      else if (fmt === 'xlsx') await exportCandidatesXlsx(rows, `${base}.xlsx`, meta);
      else await exportCandidatesPdf(rows, `${base}.pdf`, meta);
    } catch (e) {
      showToast({ title: e instanceof Error ? e.message : 'Export failed.', kind: 'error' });
    }
  };

  const handleDeleteCandidates = async (ids: string[]) => {
    if (!user?.orgId) return;
    if (!perms.canDeleteCandidate) {
      showToast({ title: 'You do not have permission to delete candidates.', kind: 'error' });
      return;
    }
    if (ids.length === 0) return;
    try {
      for (const id of ids) {
        await candidateService.softDelete(id);
      }
      setSelectedIds(new Set());
      await queryClient.invalidateQueries({ queryKey: queryKeys.candidates.all(user.orgId) });
    } catch (e) {
      showToast({ title: e instanceof Error ? e.message : 'Delete failed.', kind: 'error' });
    }
  };

  const openInterviewPrep = async () => {
    if (!orgId || selectedIds.size !== 1 || !user?.orgId) return;
    const candidateId = Array.from(selectedIds)[0]!;
    setInterviewPrepBusy(true);
    try {
      const { access_token } = await aiInterviewSessionService.createSession({
        orgId: user.orgId,
        candidateId,
        language: 'en-IN',
        sessionType: 'screening',
        createdByOrgUserId: user.orgUserId ?? null,
      });
      const url = `${window.location.origin}/interview/prep/${access_token}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      showToast({ title: e instanceof Error ? e.message : 'Could not start interview prep.', kind: 'error' });
    } finally {
      setInterviewPrepBusy(false);
    }
  };

  const fieldAccess = user?.orgUserId
    ? normalizeCandidateListUserAccess(tablePolicy.userFieldAccess[user.orgUserId])
    : undefined;
  const effectiveMaskEmail = tablePolicy.maskEmail && !fieldAccess?.viewEmail;
  const effectiveMaskPhone = tablePolicy.maskPhone && !fieldAccess?.viewPhone;
  const hideBase = (k: BaseColumnKey) => tablePolicy.hiddenBaseColumns.includes(k);

  const renderExtraColumnCell = (c: Candidate, col: ExtraColumnKey) => {
    switch (col) {
      case 'phone':
        return maskPhoneValue(c.phone, effectiveMaskPhone);
      case 'linkedin_url':
        return c.linkedin_url ? (
          <a href={c.linkedin_url} className="text-brand-400 hover:underline" target="_blank" rel="noreferrer">
            Link
          </a>
        ) : (
          '—'
        );
      case 'notes':
        if (fieldAccess?.viewNotes) return c.notes ?? '—';
        return c.notes ? '••••' : '—';
      case 'tags':
        return (c.tags?.length ?? 0) > 0 ? (c.tags ?? []).join(', ') : '—';
      case 'years_exp':
        return c.years_exp != null ? String(c.years_exp) : '—';
      case 'recruiter_rating':
        return c.recruiter_rating != null ? String(c.recruiter_rating) : '—';
      default:
        return '—';
    }
  };

  // Phase 8 T-22 §27.B — render the Assistant intake when /candidates is
  // in its pre-thread empty state (no rows, no search). Submit is wired
  // to the real downstream runner via agentRunnerService.
  if (!loading && candidates.length === 0 && !search && candidatesIntakeCfg) {
    return (
      <AssistantIntake
        greeting={candidatesIntakeCfg.greeting}
        placeholder={candidatesIntakeCfg.placeholder}
        scopePill={candidatesIntakeCfg.scopePill}
        suggestions={candidatesIntakeCfg.suggestions}
        beta={candidatesIntakeCfg.beta}
        entityPlural={candidatesIntakeCfg.entityPlural}
        firstName={(user?.name ?? user?.email ?? 'there').split(/[ @]/)[0] || 'there'}
        gradient="intake"
        onSubmit={(text) => setSearch(text)}
      />
    );
  }

  return (
    <div className="ggj-candidates-page">
      {toast}
      <SyncStatusBadge className="mb-3" />
        <PageHeader
        variant="minimal"
        breadcrumb={[{ label: 'Data', href: '/candidates' }, { label: 'Candidates' }]}
        breadcrumbRowEnd={
          <CandidatesOperationsDropdown
            objectHistoryHref={objectHistoryUrl('candidates')}
            onExport={(fmt, scope) => void runExportFormat(fmt, scope === 'selection' ? 'selection' : 'all')}
            extraActions={[
              {
                id: 'configure',
                label: 'Configure',
                icon: <Columns3 className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () => setCustomizeOpen(true),
              },
              {
                id: 'import',
                label: 'Import',
                icon: <Upload className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () => {
                  if (!orgId) return;
                  setImportModalMode('menu');
                  setImportChoiceOpen(true);
                },
              },
              {
                id: 'rights',
                label: 'Manage Rights',
                icon: <User className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () => setRightsOpen(true),
              },
              {
                id: 'ai-brief',
                label: 'AI sourcing brief',
                icon: <Sparkles className="vscode-text-secondary" strokeWidth={2} />,
                onClick: () => setSourcingAiOpen(true),
              },
              {
                id: 'interview-prep',
                label: 'Start interview prep',
                icon: <Video className="vscode-text-secondary" strokeWidth={2} />,
                disabled: !perms.canEditCandidate || selectedIds.size !== 1 || interviewPrepBusy || !orgId,
                onClick: () => void openInterviewPrep(),
              },
            ]}
          />
        }
      />

      {/* a) Filter: search - always visible when not loading */}
      {!loading && (
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            className="input pl-9"
            placeholder="Search by name, email or company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      )}

      {loading ? (
        <FullPageSpinner />
      ) : candidates.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No candidates yet"
          description="Add your first candidate to start building your talent pool."
          action={
            <Link to="/candidates/new" className="btn-primary">
              <Plus className="w-4 h-4" /> Add candidate
            </Link>
          }
        />
      ) : (
        <div className="card overflow-hidden p-0">
          {/* b) Toolbar */}
          <input
            ref={importFileRef}
            type="file"
            accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            className="hidden"
            aria-hidden
            onChange={(ev) => void onImportFile(ev)}
          />
          <DataTableToolbar hasTable={true} newLabel="New candidate" newHref="/candidates/new" />
          <DataTableDeleteConfirmModal
            open={bulkDeleteOpen}
            onClose={() => setBulkDeleteOpen(false)}
            entityLabel="candidates"
            count={selectedIds.size}
            bodyExtra="Candidates are soft-deleted from the active list."
            onConfirm={() => void handleDeleteCandidates(Array.from(selectedIds)).then(() => setBulkDeleteOpen(false))}
          />
          {/* CAN-030: bulk tag action */}
          {selectedIds.size > 0 && (
            <div className="ggj-data-bulk-bar flex flex-wrap items-center gap-2 px-4 py-2">
              {perms.canEditCandidate && (
                <button
                  type="button"
                  className="btn-secondary text-xs inline-flex items-center gap-1 px-2 py-1"
                  onClick={() => {
                    if (selectedIds.size !== 1) {
                      showToast({ title: 'Select exactly one candidate to edit.', kind: 'error' });
                      return;
                    }
                    navigate(`/candidates/${Array.from(selectedIds)[0]!}`);
                  }}
                >
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
              )}
              {perms.canDeleteCandidate && (
                <button
                  type="button"
                  className="btn-secondary text-xs inline-flex items-center gap-1 px-2 py-1"
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              )}
              <Tag className="w-3.5 h-3.5 text-gray-400" />
              <input
                className="input text-xs py-1 w-40"
                placeholder="Tag name…"
                value={bulkTagInput}
                onChange={(e) => setBulkTagInput(e.target.value)}
              />
              <button
                className="btn-secondary text-xs py-1 px-2"
                disabled={bulkTagging || !bulkTagInput.trim()}
                onClick={async () => {
                  if (!user?.orgId || !bulkTagInput.trim()) return;
                  setBulkTagging(true);
                  try {
                    await candidateTagService.bulkAddTag(user.orgId, Array.from(selectedIds), bulkTagInput.trim(), user.name);
                    setBulkTagInput('');
                    setSelectedIds(new Set());
                    await queryClient.invalidateQueries({ queryKey: queryKeys.candidates.all(user.orgId) });
                  } finally {
                    setBulkTagging(false);
                  }
                }}
              >
                {bulkTagging ? 'Tagging…' : `Tag ${selectedIds.size} selected`}
              </button>
              {selectedIds.size === 2 && (
                <button
                  className="btn-secondary text-xs py-1 px-2 ml-1"
                  onClick={() => setShowMergeModal(true)}
                  title="Merge these two candidates into one record"
                >
                  <GitMerge className="w-3 h-3" /> Merge duplicates
                </button>
              )}
              {perms.canSendEmail && (
                <button
                  type="button"
                  className="btn-secondary text-xs py-1 px-2"
                  title="Opens your mail client with BCC filled (candidate emails)"
                  onClick={handleBulkMailto}
                >
                  <Mail className="w-3 h-3" /> Email (BCC)
                </button>
              )}
              <button
                type="button"
                className="btn-secondary text-xs py-1 px-2"
                title="Deterministic skill overlap vs one job (creates application if needed)"
                onClick={() => setShowBulkFitmentModal(true)}
              >
                Skill fit vs job…
              </button>
              {perms.canUpdateApplicationStatus && (
                <button
                  type="button"
                  className="btn-secondary text-xs py-1 px-2"
                  onClick={() => void openBulkPipelineModal()}
                >
                  <GitBranch className="w-3 h-3" /> Pipeline…
                </button>
              )}
            </div>
          )}
          {/* c) Content: table with selection — horizontal scroll when many columns */}
          <div className="ggj-data-table-wrap min-w-0">
            <table className="ggj-data-table ggj-data-table--candidates ggj-data-table--wide">
            <thead>
              <tr className="border-b border-gray-800 text-left">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    ref={headerCheckRef}
                    checked={candidates.length > 0 && selectedIds.size === candidates.length}
                    onChange={() => {
                      if (selectedIds.size === candidates.length) setSelectedIds(new Set());
                      else setSelectedIds(new Set(candidates.map((c) => c.id)));
                    }}
                    className="rounded border-gray-600"
                  />
                </th>
                <SortableTh
                  label="Name"
                  column="full_name"
                  current={sortBy}
                  asc={sortAsc}
                  onSort={handleSortColumn}
                />
                {!hideBase('email') && (
                  <SortableTh
                    label="Email"
                    column="email"
                    current={sortBy}
                    asc={sortAsc}
                    onSort={handleSortColumn}
                    className="hidden md:table-cell"
                  />
                )}
                {!hideBase('current_title') && (
                  <SortableTh
                    label="Current title"
                    column="current_title"
                    current={sortBy}
                    asc={sortAsc}
                    onSort={handleSortColumn}
                    className="hidden sm:table-cell"
                  />
                )}
                {!hideBase('current_company') && (
                  <SortableTh
                    label="Company"
                    column="current_company"
                    current={sortBy}
                    asc={sortAsc}
                    onSort={handleSortColumn}
                    className="hidden sm:table-cell"
                  />
                )}
                {!hideBase('location') && (
                  <SortableTh
                    label="Location"
                    column="location"
                    current={sortBy}
                    asc={sortAsc}
                    onSort={handleSortColumn}
                    className="hidden md:table-cell"
                  />
                )}
                {!hideBase('source') && (
                  <SortableTh
                    label="Source"
                    column="source_channel"
                    current={sortBy}
                    asc={sortAsc}
                    onSort={handleSortColumn}
                    className="hidden lg:table-cell"
                  />
                )}
                {!hideBase('skills') && (
                  <th className="hidden lg:table-cell px-4 py-3 uppercase text-xs tracking-wide font-semibold vscode-text-primary">Skills</th>
                )}
                {!hideBase('rolefit') && (
                  <th className="hidden md:table-cell px-4 py-3 uppercase text-xs tracking-wide font-semibold vscode-text-primary">Role-fit</th>
                )}
                {!hideBase('resume') && (
                  <th className="px-4 py-3 uppercase text-xs tracking-wide font-semibold vscode-text-primary">Resume</th>
                )}
                {!hideBase('created') && (
                  <SortableTh
                    label="Created when"
                    column="created_at"
                    current={sortBy}
                    asc={sortAsc}
                    onSort={handleSortColumn}
                    className="hidden md:table-cell whitespace-nowrap"
                  />
                )}
                {tablePolicy.extraColumns.map((col) => {
                  const meta = EXTRA_COLUMN_META.find((m) => m.key === col);
                  return (
                    <th key={col} className="px-3 py-0 text-[11px] font-medium vscode-text-secondary whitespace-nowrap">
                      {meta?.label ?? col}
                    </th>
                  );
                })}
                <EnrichColumnTh />
              </tr>
            </thead>
            <tbody>
              {candidates.map((c) => (
                <tr
                  key={c.id}
                  tabIndex={0}
                  className={[
                    selectedIds.has(c.id) ? 'bg-brand-500/10' : '',
                    'cursor-pointer outline-none transition-colors hover:bg-[color-mix(in_srgb,var(--vscode-bg-hover)_65%,transparent)] focus-visible:ring-2 focus-visible:ring-brand-500/40',
                  ].join(' ')}
                  onClick={() => setPreviewId(c.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setPreviewId(c.id);
                    }
                    if (e.key === 'ArrowRight') {
                      e.preventDefault();
                      setPreviewId(c.id);
                    }
                    if (e.key === 'ArrowLeft' && previewId) {
                      e.preventDefault();
                      setPreviewId(null);
                    }
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setRowMenu({ x: e.clientX, y: e.clientY, candidateId: c.id });
                  }}
                >
                  <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(c.id)}
                      onChange={() => {
                        const next = new Set(selectedIds);
                        if (next.has(c.id)) next.delete(c.id);
                        else next.add(c.id);
                        setSelectedIds(next);
                      }}
                      className="rounded border-gray-600"
                    />
                  </td>
                  <td className="px-3 py-0">
                    <div className="flex items-center gap-2">
                      <TableAvatar name={c.full_name} size={20} />
                      <p className="text-[13px] font-medium vscode-text-primary truncate">{c.full_name}</p>
                    </div>
                  </td>
                  {!hideBase('email') && (
                    <td className="px-4 py-3 hidden md:table-cell text-sm vscode-text-secondary font-medium">
                      {maskEmailValue(c.email, effectiveMaskEmail)}
                    </td>
                  )}
                  {!hideBase('current_title') && (
                    <td className="px-4 py-3 hidden sm:table-cell font-medium vscode-text-primary">{c.current_title ?? '—'}</td>
                  )}
                  {!hideBase('current_company') && (
                    <td className="px-4 py-3 hidden sm:table-cell text-sm vscode-text-secondary font-medium">{c.current_company ?? '—'}</td>
                  )}
                  {!hideBase('location') && (
                    <td className="px-4 py-3 vscode-text-secondary hidden md:table-cell font-medium">{c.location ?? '—'}</td>
                  )}
                  {!hideBase('source') && (
                    <td className="px-4 py-3 text-xs vscode-text-secondary hidden lg:table-cell font-medium">{c.source_channel ?? '—'}</td>
                  )}
                  {!hideBase('skills') && (
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(c.parsed_skills ?? []).slice(0, 3).map((s) => (
                          <span
                            key={s}
                            className="px-1.5 py-0.5 text-xs rounded border vscode-border bg-[color-mix(in_srgb,var(--vscode-bg-tertiary)_55%,transparent)] vscode-text-secondary font-medium"
                          >
                            {s}
                          </span>
                        ))}
                        {(c.parsed_skills?.length ?? 0) > 3 && (
                          <span className="text-xs vscode-text-tertiary font-medium">+{(c.parsed_skills?.length ?? 0) - 3}</span>
                        )}
                      </div>
                    </td>
                  )}
                  {!hideBase('rolefit') && (
                    <td className="px-4 py-3 hidden md:table-cell">
                      {roleFitJobByCandidate[c.id] ? (
                        <Link
                          to={`/role-fit/${c.id}/${roleFitJobByCandidate[c.id]}`}
                          className="text-xs font-semibold text-brand-400 hover:text-brand-300"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Open
                        </Link>
                      ) : (
                        <span className="text-xs vscode-text-tertiary font-medium" title="No application yet — role-fit needs a job in context">
                          —
                        </span>
                      )}
                    </td>
                  )}
                  {!hideBase('resume') && (
                    <td className="px-4 py-3">
                      {c.base_resume_url ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                          <Upload className="w-3 h-3" /> Uploaded
                        </span>
                      ) : (
                        <span className="text-xs vscode-text-tertiary font-medium">None</span>
                      )}
                    </td>
                  )}
                  {!hideBase('created') && (
                    <td className="px-4 py-3 text-sm vscode-text-secondary hidden md:table-cell tabular-nums whitespace-nowrap font-medium">
                      {formatCreatedWhen(c.created_at)}
                    </td>
                  )}
                  {tablePolicy.extraColumns.map((col) => (
                    <td key={col} className="px-3 py-0 text-[13px] vscode-text-secondary max-w-[200px] truncate">
                      {renderExtraColumnCell(c, col)}
                    </td>
                  ))}
                  <RowHoverActions />
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <TablePagination
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            entityLabel="candidates"
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        </div>
      )}

      {/* CAN-040: merge modal (appears when exactly 2 are selected) */}
      <Modal
        open={showBulkPipelineModal}
        onClose={() => {
          setShowBulkPipelineModal(false);
          setBulkAppIds([]);
          setBulkPipelineErr('');
        }}
        title="Bulk pipeline update"
        subtitle={`Move ${selectedIds.size} candidate${selectedIds.size !== 1 ? 's' : ''} to a new stage`}
        icon={<GitBranchPlus className="w-4 h-4" />}
        size="lg"
      >
        <div className="px-5 py-4 space-y-4 text-sm">
          {bulkPipelineErr && (
            <p className="text-red-400 text-sm">{bulkPipelineErr}</p>
          )}
          {bulkAppIds.length > 0 && (
            <>
              <p className="text-gray-400">
                This will set <strong className="text-gray-200">{bulkAppIds.length}</strong> application
                {bulkAppIds.length === 1 ? '' : 's'} (for selected candidates) to one stage.
              </p>
              <div>
                <label className="label">New stage</label>
                <select
                  className="input"
                  value={bulkPipelineStatus}
                  onChange={(e) => setBulkPipelineStatus(e.target.value as ApplicationStatus)}
                >
                  {ALL_STATUSES.map((s) => (
                    <option key={s} value={s}>{PIPELINE_STATUS_LABEL[s]}</option>
                  ))}
                </select>
              </div>
              {STATUSES_REQUIRING_REJECTION.includes(bulkPipelineStatus) && (
                <div className="space-y-2">
                  <label className="label">Rejection reason</label>
                  <select
                    className="input"
                    value={bulkRejectionCode}
                    onChange={(e) => setBulkRejectionCode(e.target.value as RejectionReasonCode)}
                  >
                    {REJECTION_REASON_CODES.map((c) => (
                      <option key={c} value={c}>{REJECTION_REASON_LABELS[c]}</option>
                    ))}
                  </select>
                  {bulkRejectionCode === 'other' && (
                    <textarea
                      className="input min-h-[72px]"
                      placeholder="Short note (required for “Other”)"
                      value={bulkRejectionText}
                      onChange={(e) => setBulkRejectionText(e.target.value)}
                    />
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    setShowBulkPipelineModal(false);
                    setBulkAppIds([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={bulkPipelineBusy}
                  onClick={async () => {
                    if (!user?.orgId || bulkAppIds.length === 0) return;
                    if (STATUSES_REQUIRING_REJECTION.includes(bulkPipelineStatus)) {
                      if (bulkRejectionCode === 'other' && !bulkRejectionText.trim()) {
                        setBulkPipelineErr('Please add a note for “Other”.');
                        return;
                      }
                    }
                    setBulkPipelineBusy(true);
                    setBulkPipelineErr('');
                    try {
                      await applicationService.bulkUpdateStatus(
                        user.orgId,
                        bulkAppIds,
                        bulkPipelineStatus,
                        STATUSES_REQUIRING_REJECTION.includes(bulkPipelineStatus)
                          ? {
                              rejectionReasonCode: bulkRejectionCode,
                              rejectionReasonText: bulkRejectionText.trim() || null,
                            }
                          : undefined
                      );
                      await queryClient.invalidateQueries({ queryKey: queryKeys.applications.all(user.orgId) });
                      await queryClient.invalidateQueries({ queryKey: queryKeys.candidates.all(user.orgId) });
                      setShowBulkPipelineModal(false);
                      setBulkAppIds([]);
                      setSelectedIds(new Set());
                    } catch (e) {
                      setBulkPipelineErr(e instanceof Error ? e.message : 'Update failed');
                    } finally {
                      setBulkPipelineBusy(false);
                    }
                  }}
                >
                  {bulkPipelineBusy ? 'Updating…' : 'Apply to all'}
                </button>
              </div>
            </>
          )}
        </div>
      </Modal>

      {showMergeModal && selectedIds.size === 2 && (() => {
        const [idA, idB] = Array.from(selectedIds);
        const candA = candidates.find((c) => c.id === idA);
        const candB = candidates.find((c) => c.id === idB);
        if (!candA || !candB || !user?.orgId) return null;
        return (
          <CandidateMergeModal
            orgId={user.orgId}
            orgUserId={user.orgUserId ?? null}
            candidateA={candA}
            candidateB={candB}
            onClose={() => setShowMergeModal(false)}
            onMerged={(_survivorId, _removedId) => {
              void queryClient.invalidateQueries({ queryKey: queryKeys.candidates.all(user.orgId!) });
              setSelectedIds(new Set());
              setShowMergeModal(false);
            }}
          />
        );
      })()}

      {user?.orgId && (
        <CandidateBulkFitmentModal
          open={showBulkFitmentModal}
          onClose={() => setShowBulkFitmentModal(false)}
          orgId={user.orgId}
          userId={user.id}
          candidateIds={Array.from(selectedIds)}
          onComplete={() => {
            if (!user.orgId) return;
            void queryClient.invalidateQueries({ queryKey: queryKeys.candidates.all(user.orgId) });
          }}
        />
      )}

      <ContextAiAssistModal
        open={sourcingAiOpen}
        onClose={() => setSourcingAiOpen(false)}
        orgId={orgId}
        feature="ai_assist_sourcing_talent_pool"
        preset="sourcing_talent_pool"
        title="AI — talent pool sourcing"
        contextHint={sourcingPoolContext}
        original=""
        showApplyActions={false}
        onApply={() => {}}
      />

      <CandidatesCustomizeColumnsModal
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        orgId={orgId ?? null}
        value={tablePolicy.extraColumns}
        onSave={(cols) => setTablePolicy((p) => ({ ...p, extraColumns: cols }))}
      />
      <CandidatesTableRightsModal
        open={rightsOpen}
        onClose={() => setRightsOpen(false)}
        orgId={orgId ?? null}
        policy={tablePolicy}
        onSave={(p) => setTablePolicy(p)}
      />

      <Modal
        open={importChoiceOpen}
        onClose={closeImportModal}
        title="Import candidates"
        subtitle={
          importModalMode === 'menu'
            ? 'Spreadsheet rows or resume files (Smart Match pipeline).'
            : 'Upload resumes — candidates created and scored against open jobs.'
        }
        icon={
          importModalMode === 'menu' ? (
            <Upload className="w-4 h-4" />
          ) : (
            <Sparkles className="w-4 h-4" />
          )
        }
        size="2xl"
      >
        {importModalMode === 'menu' ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm vscode-text-secondary leading-relaxed">
              Import a CSV or Excel template to create many candidate rows at once, or upload resume documents for AI
              parsing, embeddings, and matching.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                className="btn-primary justify-center inline-flex items-center gap-2"
                onClick={() => {
                  setImportChoiceOpen(false);
                  setImportModalMode('menu');
                  openImportPicker();
                }}
              >
                Spreadsheet (CSV / Excel)
              </button>
              <button
                type="button"
                className="btn-secondary justify-center inline-flex items-center gap-2"
                onClick={() => setImportModalMode('resumes')}
              >
                <Sparkles className="w-4 h-4" />
                Resume files (Smart Match)
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <button type="button" className="btn-secondary text-xs" onClick={() => setImportModalMode('menu')}>
              ← Back
            </button>
            <div className="max-h-[min(72vh,720px)] overflow-y-auto -mx-1 px-1">
              <SmartMatchRedirectCTA
                title="Bulk resume uploads moved to Smart Match"
                subtitle="Drop up to 100 resumes on the Smart Match canvas — the agent parses, scores and buckets them in a single flow."
              />
            </div>
          </div>
        )}
      </Modal>

      {previewId ? (
        (() => {
          const c = candidates.find((x) => x.id === previewId);
          if (!c) return null;
          return (
            <ListPreviewDrawer
              open
              onClose={() => setPreviewId(null)}
              typeLabel="Candidate"
              title={c.full_name}
              subtitle={[c.current_title, c.current_company].filter(Boolean).join(' @ ') || undefined}
              openFullHref={`/candidates/${c.id}`}
            >
              <div className="space-y-4">
                <RecordHeader
                  avatarInitials={previewInitials(c.full_name)}
                  title={c.full_name}
                  meta={[c.current_title, c.current_company].filter(Boolean).join(' @ ') || c.email || undefined}
                />
                <RecordMetaCard
                  title="Summary"
                  rows={[
                    { label: 'Created', value: formatDateTimeGmt(c.created_at) },
                    { label: 'Updated', value: formatDateTimeGmt(c.updated_at) },
                    {
                      label: 'Source',
                      value: c.source
                        ? (CANDIDATE_SOURCE_LABELS[c.source as CandidateSource] ?? c.source)
                        : (c.source_channel ?? '—'),
                    },
                    { label: 'Email', value: c.email },
                    { label: 'Years exp', value: c.years_exp ?? '—' },
                    ...(c.parsed_skills?.length
                      ? [{ label: 'Top skills', value: (c.parsed_skills ?? []).slice(0, 8).join(', ') }]
                      : []),
                  ]}
                />
              </div>
            </ListPreviewDrawer>
          );
        })()
      ) : null}

      {rowMenu ? (
        <div
          ref={rowMenuRef}
          className="fixed z-[160] min-w-[200px] rounded-lg border vscode-border vscode-bg-elevated py-1 shadow-xl"
          style={{ left: rowMenu.x, top: rowMenu.y }}
          role="menu"
        >
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-sm vscode-text-primary hover:vscode-bg-hover"
            onClick={() => {
              setPreviewId(rowMenu.candidateId);
              setRowMenu(null);
            }}
          >
            Open preview
          </button>
          <button
            type="button"
            className="block w-full px-3 py-2 text-left text-sm vscode-text-primary hover:vscode-bg-hover"
            onClick={() => {
              navigate(`/candidates/${rowMenu.candidateId}`);
              setRowMenu(null);
            }}
          >
            Open in full page
          </button>
        </div>
      ) : null}

      {/* §28.B — Bulk compose panel: slides up when ≥ 2 rows are selected */}
      {selectedIds.size >= 2 ? (
        <BulkComposePanel
          recipients={Array.from(selectedIds).map((id) => {
            const c = candidates.find((x) => x.id === id);
            return { id, name: c?.full_name ?? '', email: c?.email ?? null };
          })}
          onClose={() => setSelectedIds(new Set())}
        />
      ) : null}
    </div>
  );
}
